/**
 * Storefront formatters — customer-facing pendant to
 * `useAdminFormatters`. Provided once at the page level via
 * `<StorefrontFormattersProvider tenant={…}>`; every nested component
 * reads it via `useStorefrontFormatters()` without prop-drilling.
 *
 * IMPORTANT — currency-of-record:
 *   The customer-facing flow shows TWO kinds of amounts:
 *     1. Service prices on the menu (live, can be re-priced any time)
 *        → use tenant.currency  → `fmt.money(price)`
 *     2. Booking amounts already locked at create-time (confirmation
 *        page, "you paid X" lines)
 *        → use booking.currency → `fmt.money(amount, { currency: row.currency })`
 *   For (2), pass the per-row currency via `options.currency` so a
 *   tenant who switches their display currency next year does NOT
 *   relabel historical bookings.
 */
'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import {
  formatMoney,
  formatMoneyCents,
  formatShortDate,
  formatLongDate,
  formatTime,
  formatDateTime,
  formatDuration,
  formatNumber,
  resolveFormatContext,
  type TenantFormatContext,
  type FormatMoneyOptions,
} from '@/lib/tenant-formatters';

export interface StorefrontTenantInput {
  /** ISO 4217 — `tenant.currency`. Defaults to 'EUR'. */
  currency?: string | null;
  /** BCP 47 — `tenant.default_locale`. Defaults to 'de'. */
  default_locale?: string | null;
  /** IANA — `tenant.timezone`. Defaults to 'Europe/Berlin'. */
  timezone?: string | null;
}

export interface StorefrontFormatters {
  ctx: TenantFormatContext;
  money: (amount: number, options?: FormatMoneyOptions) => string;
  moneyCents: (cents: number, options?: FormatMoneyOptions) => string;
  date: (date: Date | number | string) => string;
  longDate: (date: Date | number | string) => string;
  time: (date: Date | number | string) => string;
  dateTime: (date: Date | number | string) => string;
  duration: (minutes: number) => string;
  number: (n: number, options?: Intl.NumberFormatOptions) => string;
}

const StorefrontFormattersContext = createContext<StorefrontFormatters | null>(null);

export function StorefrontFormattersProvider({
  tenant,
  children,
}: {
  tenant: StorefrontTenantInput | null | undefined;
  children: ReactNode;
}) {
  const value = useMemo<StorefrontFormatters>(() => {
    const ctx = resolveFormatContext({
      currency: tenant?.currency,
      default_locale: tenant?.default_locale,
      timezone: tenant?.timezone,
    });
    return {
      ctx,
      money: (amount, options) => formatMoney(amount, ctx, options),
      moneyCents: (cents, options) => formatMoneyCents(cents, ctx, options),
      date: (d) => formatShortDate(d, ctx),
      longDate: (d) => formatLongDate(d, ctx),
      time: (d) => formatTime(d, ctx),
      dateTime: (d) => formatDateTime(d, ctx),
      duration: (m) => formatDuration(m, ctx),
      number: (n, options) => formatNumber(n, ctx, options),
    };
  }, [tenant?.currency, tenant?.default_locale, tenant?.timezone]);
  return (
    <StorefrontFormattersContext.Provider value={value}>
      {children}
    </StorefrontFormattersContext.Provider>
  );
}

export function useStorefrontFormatters(): StorefrontFormatters {
  const ctx = useContext(StorefrontFormattersContext);
  if (!ctx) {
    // Soft fallback — components used outside the provider get
    // platform defaults. Logs are easier to read than a thrown error
    // because the storefront booking flow may render in preview
    // contexts (embedded widget snippets) that don't always wrap.
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        '[useStorefrontFormatters] called outside StorefrontFormattersProvider — using EUR/de/Europe/Berlin defaults.',
      );
    }
    const fallback = resolveFormatContext({});
    return {
      ctx: fallback,
      money: (a, o) => formatMoney(a, fallback, o),
      moneyCents: (c, o) => formatMoneyCents(c, fallback, o),
      date: (d) => formatShortDate(d, fallback),
      longDate: (d) => formatLongDate(d, fallback),
      time: (d) => formatTime(d, fallback),
      dateTime: (d) => formatDateTime(d, fallback),
      duration: (m) => formatDuration(m, fallback),
      number: (n, o) => formatNumber(n, fallback, o),
    };
  }
  return ctx;
}
